/**
 * Мобильный хаб мастера (task 71 §6.1 — точка входа лида на телефоне).
 *
 * Минимальный каркас: бренд-полоса + две ветки — «Подтверждение» (рабочее место
 * руководителя, §5.x → пока десктоп-`/time-approval`) и «Моё время» (свой
 * таймшит). §6.2 развернёт это в полный M6 (3 экрана подтверждения + общий
 * стейт с десктоп-компонентами). Здесь — рабочая навигационная заглушка, чтобы
 * редирект §6.1 приземлялся в PWA, а не в office-`/home`.
 *
 * Свой mobile-shell (не worker-`FieldShell`: у того logout через worker-эндпоинт,
 * а у мастера office-сессия).
 */
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { ClipboardCheck, Clock, ListChecks, LogOut } from "lucide-react";

import { logout as logoutApi } from "@/api/auth";
import { useInvalidateMe, useMe } from "@/hooks/useMe";
import { useSession } from "@/store/session";

export function LeadHubPage(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useMe();
  const clear = useSession((s) => s.clear);
  const invalidateMe = useInvalidateMe();

  const logout = useMutation({
    mutationFn: () => logoutApi(),
    onSettled: () => {
      clear();
      invalidateMe();
      void navigate({ to: "/login" });
    },
  });

  return (
    <div className="flex min-h-screen flex-col bg-[var(--bg-panel-1)]">
      <header className="flex h-12 items-center justify-between border-b border-[var(--rule-line)] bg-[var(--bg-panel-0)] px-4">
        <span className="font-display text-sm font-semibold tracking-[0.04em] text-[var(--fg-1)]">
          GUDWIN
        </span>
        <button
          type="button"
          data-testid="lead-hub-logout"
          aria-label={t("leadHub.logout")}
          onClick={() => logout.mutate()}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--fg-3)]"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {t("leadHub.logout")}
        </button>
      </header>

      <main className="flex flex-1 flex-col gap-4 p-4" data-testid="lead-hub">
        <div className="flex flex-col gap-0.5">
          <h1 className="font-display text-xl font-semibold text-[var(--fg-1)]">
            {t("leadHub.title")}
          </h1>
          {user?.display_name && (
            <span className="text-sm text-[var(--fg-3)]">{user.display_name}</span>
          )}
        </div>

        <HubCard
          icon={<ClipboardCheck className="h-6 w-6" aria-hidden />}
          title={t("leadHub.approval")}
          subtitle={t("leadHub.approvalHint")}
          testId="lead-hub-approval"
          onClick={() => void navigate({ to: "/field/lead/approve" })}
        />
        <HubCard
          icon={<ListChecks className="h-6 w-6" aria-hidden />}
          title={t("leadHub.workStatus")}
          subtitle={t("leadHub.workStatusHint")}
          testId="lead-hub-work-status"
          onClick={() => void navigate({ to: "/field/lead/work-status" })}
        />
        <HubCard
          icon={<Clock className="h-6 w-6" aria-hidden />}
          title={t("leadHub.myTime")}
          subtitle={t("leadHub.myTimeHint")}
          testId="lead-hub-my-time"
          onClick={() => void navigate({ to: "/field/lead/approve", search: { mytime: true } })}
        />
      </main>
    </div>
  );
}

function HubCard({
  icon,
  title,
  subtitle,
  testId,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  testId: string;
  onClick: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex items-center gap-3 rounded-[var(--radius-card)] border border-[var(--rule-line)] bg-[var(--bg-panel-0)] p-4 text-left active:bg-[var(--bg-panel-1)]"
    >
      <span
        className="flex size-11 shrink-0 items-center justify-center rounded-[var(--radius-card)]"
        style={{ background: "var(--bg-panel-1)", color: "var(--accent-tech)" }}
      >
        {icon}
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-base font-semibold text-[var(--fg-1)]">{title}</span>
        <span className="text-xs text-[var(--fg-3)]">{subtitle}</span>
      </span>
    </button>
  );
}
